import { Component, OnInit } from '@angular/core';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import {
  NCycle,
  NCycleResult,
  NSimulationResults,
  SimulationResponse,
  SimulationResults,
} from '../../../core/models/simulation.model';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import Highcharts from 'highcharts';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { HighchartsChartModule } from 'highcharts-angular';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { SimulationDirectionsTableComponent } from '../../simulation-directions-table/simulation-directions-table.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import { map, merge } from 'rxjs';
import { 
  createBaseLogChartOptions, 
  createScatterSeries, 
  createTrendLineSeries,
  updateChartSeriesVisibilitySmooth,
  MARKER_SYMBOLS 
} from '../../../utils/chart/chart-config.util';
import { formatValue } from '../../../utils/format.util';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';

@Component({
  selector: 'app-saturation-exponent-results',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatExpansionModule,
    HighchartsChartModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatTableModule,
    SimulationOverviewComponent,
    SimulationDirectionsTableComponent,
  ],
  templateUrl: './saturation-exponent-results.component.html',
  styleUrl: './saturation-exponent-results.component.scss',
  standalone: true,
})
export class SaturationExponentResultsComponent extends Destroyable(Object) implements OnInit {
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly componentId: string = 'n-simulation-results';
  readonly primaryDrainageColor: string = '#4F81BD';
  readonly firstImbibitionColor: string = '#C0504D';
  readonly secondaryDrainageColor: string = '#8064A2';
  readonly defaultColor: string = '#4A4A4A';
  readonly cycles: NCycle[] = ['pd', 'fi', 'sd'];
  simulationResponse: SimulationResponse<NSimulationResults> | null = null;
  cyclesForm!: FormGroup;
  chart?: Highcharts.Chart;
  chartOptions!: Highcharts.Options;
  displayedColumns: string[] = [];
  tableData: any[] = [];
  nTableColumns: string[] = [];
  nTableData: any[] = [];
  isLoading: boolean = false;
  capillaryPressure: string | null = null;
  parentSimulationId: string | null = null;

  get pageTitle(): string {
    const prefix =
      this.simulationResponse?.type === SimulatorType.UpscalingN
        ? 'Upscaling - '
        : 'Subsample - ';
    return prefix + 'Saturation Exponent Results';
  }

  get isUpscaling(): boolean {
    return this.simulationResponse?.type === SimulatorType.UpscalingN;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly fb: FormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.initCyclesForm();

    const simulationId: string | null = this.route.snapshot.paramMap.get('id');
    if (simulationId) {
      this.loadSimulation(simulationId);
    }
  }

  get results(): NSimulationResults[] | undefined {
    return this.simulationResponse?.results;
  }

  get simulationResult(): NSimulationResults | undefined {
    return this.results?.[0];
  }

  get simulationDirection(): string {
    return this.simulationResult?.direction ?? '';
  }

  get pdNResult(): NCycleResult | undefined {
    return this.simulationResult?.result?.pdNResult;
  }

  get fiNResult(): NCycleResult | undefined {
    return this.simulationResult?.result?.fiNResult;
  }

  get sdNResult(): NCycleResult | undefined {
    return this.simulationResult?.result?.sdNResult;
  }

  getCycleControl(cycle: NCycle): FormControl {
    return this.cyclesForm.get(cycle) as FormControl;
  }

  isNResultsDataValid(): boolean {
    return !!this.results && this.results.length > 0;
  }

  isSimulationResultValid(): boolean {
    return this.simulationResult?.status?.toLowerCase() === 'success';
  }

  isCycleResultExists(cycle: NCycle): boolean {
    if (!this.simulationResult) return false;

    const cycleResult: NCycleResult | undefined = this.getCycleResult(cycle);
    return Boolean(cycleResult?.swCrop?.length && cycleResult?.riAvg?.length);
  }

  getCycleResult(cycle: NCycle): NCycleResult | undefined {
    switch (cycle) {
      case 'pd':
        return this.pdNResult;
      case 'fi':
        return this.fiNResult;
      case 'sd':
        return this.sdNResult;
    }
  }

  saveChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    // Initialize chart with all series upfront for smooth visibility toggling
    this.initializeChartSeries();
  }

  private initializeChartSeries(): void {
    if (!this.chart) return;

    const cycleOrder: NCycle[] = ['pd', 'fi', 'sd'];

    // Add all possible series to the chart upfront
    cycleOrder.forEach((cycle) => {
      if (this.isCycleResultExists(cycle)) {
        const dataSeries = this.createChartSeriesByCycleType(cycle);
        const fitSeries = this.createFitSeriesByCycleType(cycle);
        this.chart!.addSeries(dataSeries, false);
        this.chart!.addSeries(fitSeries, false);
      }
    });

    this.chart.redraw();

    // Apply initial visibility based on form state
    this.updateChartSeriesVisibilitySmooth();
  }

  private updateChartSeriesInOrder(): void {
    if (!this.chart) return;
    this.updateChartSeriesVisibilitySmooth();
  }

  private updateChartSeriesVisibilitySmooth(): void {
    if (!this.chart) return;

    const cycleOrder: NCycle[] = ['pd', 'fi', 'sd'];

    // Determine which series should be visible based on selection
    const selectedSeriesNames = new Set<string>();
    cycleOrder.forEach((cycle) => {
      if (this.getCycleControl(cycle).value && this.isCycleResultExists(cycle)) {
        const dataSeries = this.createChartSeriesByCycleType(cycle);
        const fitSeries = this.createFitSeriesByCycleType(cycle);
        selectedSeriesNames.add(dataSeries.name as string);
        selectedSeriesNames.add(fitSeries.name as string);
      }
    });

    updateChartSeriesVisibilitySmooth(this.chart, selectedSeriesNames);
  }

  getColumnStyle(columnName: string): Record<string, string> {
    return {
      'background': this.defineColorForTable(columnName),
      'width': `${100 / (this.displayedColumns.length || 1)}%`,
    };
  }

  downloadResults(): void {
    if (this.simulationResponse && this.checkIfAtLeastOneCycleSelected()) {
      const params: Record<string, boolean> = {
        ...this.getSelectedDirections(),
        ...this.getSelectedCycles(),
      };
      this.simulationsService.downloadResult(this.simulationResponse.id, params);
    }
  }

  private loadSimulation(id: string): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(id, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((simulation: SimulationResponse<SimulationResults>) => {
        this.simulationResponse = simulation as SimulationResponse<NSimulationResults>;

        if (this.isNResultsDataValid() && this.isSimulationResultValid()) {
          this.configureCyclesFormControls();
          this.initChart();
          this.updateNTable();
          this.updateTable();
        }

        // Load parent simulation (Capillary Pressure) if exists
        const parentSimulationId = this.simulationResponse?.parameters?.parentSimulationId;
        if (parentSimulationId) {
          this.parentSimulationId = parentSimulationId;
          this.loadParentSimulation(parentSimulationId);
        }
      });
  }

  private loadParentSimulation(parentSimulationId: string): void {
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(parentSimulationId, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((parentSimulation: SimulationResponse<SimulationResults>) => {
        this.capillaryPressure = parentSimulation.name;
      });
  }

  openParentSimulation(): void {
    if (this.parentSimulationId) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/simulation-results/pc', this.parentSimulationId])
      );
      window.open(url, '_blank');
    }
  }

  private configureCyclesFormControls(): void {
    this.cycles.forEach((cycle: NCycle) => {
      // SD option is always disabled
      if (cycle === 'sd') {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
        return;
      }

      const cycleResultExists: boolean = this.isCycleResultExists(cycle);

      if (cycleResultExists) {
        this.getCycleControl(cycle).patchValue(true, { emitEvent: false });
        this.getCycleControl(cycle).enable({ emitEvent: false });
      } else {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
      }
    });
  }

  private initChart(): void {
    this.chartOptions = this.createLogChart(
      'Saturation Exponent (n)',
      'Saturation (fraction)',
      'Resistivity Index (RI)',
    );
  }

  private createLogChart(
    title: string,
    xAxisTitle: string,
    yAxisTitle: string,
  ): Highcharts.Options {
    return createBaseLogChartOptions({
      title,
      xAxisTitle,
      yAxisTitle,
      series: [], // Start with empty series
      xAxisType: 'logarithmic',
      yAxisType: 'logarithmic',
      tooltipFormatter: function() {
        return `<b>${this.series.name}</b><br/><b>Sw:</b> ${this.x?.toFixed(2)}<br/><b>RI:</b> ${this.y?.toFixed(2)}`;
      },
    });
  }

  private initChartSeries(): Highcharts.SeriesOptionsType[] {
    const directionSeries: Highcharts.SeriesOptionsType[] = [];
    const isPDEnabled: boolean = this.getCycleControl('pd').value;
    const isFIEnabled: boolean = this.getCycleControl('fi').value;
    const isSDEnabled: boolean = this.getCycleControl('sd').value;

    if (isPDEnabled) {
      const pdSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('pd');
      const pdFitSeries: Highcharts.SeriesOptionsType = this.createFitSeriesByCycleType('pd');
      directionSeries.push(pdSeries, pdFitSeries);
    }

    if (isFIEnabled) {
      const fiSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('fi');
      const fiFitSeries: Highcharts.SeriesOptionsType = this.createFitSeriesByCycleType('fi');
      directionSeries.push(fiSeries, fiFitSeries);
    }

    if (isSDEnabled) {
      const sdSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('sd');
      const sdFitSeries: Highcharts.SeriesOptionsType = this.createFitSeriesByCycleType('sd');
      directionSeries.push(sdSeries, sdFitSeries);
    }

    return directionSeries;
  }

  private createChartSeriesByCycleType(cycle: NCycle): Highcharts.SeriesOptionsType {
    const cycleResult: NCycleResult | undefined = this.getCycleResult(cycle);
    const cycleName: string = this.getCycleName(cycle);
    const directionLabel: string = this.getDirectionLabel();

    if (!cycleResult) {
      return {} as Highcharts.SeriesOptionsType;
    }

    const color: string = this.defineColorForCycleType(cycle);
    const name: string = `${cycleName} - RI (${directionLabel})`;
    const markerSymbol: string = cycle === 'fi' ? MARKER_SYMBOLS.TRIANGLE : MARKER_SYMBOLS.CIRCLE;

    // Use SwCrop for X and RIAvg for Y
    const data: number[][] = cycleResult.swCrop.map((saturation: string, index: number) => {
      return [Number(saturation), Number(cycleResult.riAvg[index])];
    });

    return createScatterSeries({
      name,
      data,
      color,
      symbol: markerSymbol,
      custom: {
        cycle,
      },
    });
  }

  private createFitSeriesByCycleType(cycle: NCycle): Highcharts.SeriesOptionsType {
    const cycleResult: NCycleResult | undefined = this.getCycleResult(cycle);
    const cycleName: string = this.getCycleName(cycle);

    if (!cycleResult || !cycleResult.swFit?.length || !cycleResult.riFit?.length) {
      return {} as Highcharts.SeriesOptionsType;
    }

    const color: string = this.defineColorForCycleType(cycle);
    const formattedN: string = cycleResult.nFit ? formatValue(cycleResult.nFit) : 'N/A';
    const name: string = `${cycleName} - n (${formattedN})`;

    // Use SwFit for X and RIFit for Y
    const data: number[][] = cycleResult.swFit.map((saturation: string, index: number) => {
      return [Number(saturation), Number(cycleResult.riFit[index])];
    });

    return createTrendLineSeries({
      name,
      data,
      color,
      custom: {
        cycle,
        isFit: true,
      },
    });
  }

  private getCycleName(cycle: NCycle): string {
    switch (cycle) {
      case 'pd':
        return 'Primary Drainage';
      case 'fi':
        return 'First Imbibition';
      case 'sd':
        return 'Secondary Drainage';
    }
  }

  private getDirectionLabel(): string {
    // Always use avg label since we use average values for all directions
    return 'avg';
  }

  private updateSeriesWhenSelectedCyclesChanged(cycle: NCycle, checked: boolean): void {
    // Regenerate all series in consistent order instead of just adding/removing
    // This ensures series order always matches table order (pd data, pd fit, fi data, fi fit, sd data, sd fit)
    this.updateChartSeriesVisibilitySmooth();
  }

  private updateNTable(): void {
    const row: Record<string, string> = {};
    const columns: string[] = [];

    if (this.getCycleControl('pd').value && this.pdNResult) {
      columns.push('PD n');
      row['PD n'] = formatValue(this.pdNResult.nFit);
    }

    if (this.getCycleControl('fi').value && this.fiNResult) {
      columns.push('FI n');
      row['FI n'] = formatValue(this.fiNResult.nFit);
    }

    if (this.getCycleControl('sd').value && this.sdNResult) {
      columns.push('SD n');
      row['SD n'] = formatValue(this.sdNResult.nFit);
    }

    this.nTableColumns = columns;
    this.nTableData = columns.length > 0 ? [row] : [];
  }

  private updateTable(): void {
    const rows: any[] = [];
    const pdResult: NCycleResult | undefined = this.getCycleControl('pd').value ? this.pdNResult : undefined;
    const fiResult: NCycleResult | undefined = this.getCycleControl('fi').value ? this.fiNResult : undefined;
    const sdResult: NCycleResult | undefined = this.getCycleControl('sd').value ? this.sdNResult : undefined;

    const maxLength: number = Math.max(
      pdResult?.swCrop?.length ?? 0,
      fiResult?.swCrop?.length ?? 0,
      sdResult?.swCrop?.length ?? 0,
    );

    const directionLabel: string = this.getDirectionLabel();

    for (let i = 0; i < maxLength; i++) {
      const row: Record<string, string> = {};

      if (pdResult) {
        row[`PD Saturation (fraction)`] = formatValue(pdResult.swCrop[i]);
        row[`PD RI (${directionLabel})`] = formatValue(pdResult.riAvg[i]);
      }

      if (fiResult) {
        row[`FI Saturation (fraction)`] = formatValue(fiResult.swCrop[i]);
        row[`FI RI (${directionLabel})`] = formatValue(fiResult.riAvg[i]);
      }

      if (sdResult) {
        row[`SD Saturation (fraction)`] = formatValue(sdResult.swCrop[i]);
        row[`SD FF (${directionLabel})`] = formatValue(sdResult.ffAvg[i]);
        row[`SD RI (${directionLabel})`] = formatValue(sdResult.riAvg[i]);
        row[`SD n (${directionLabel})`] = formatValue(sdResult.nAvg[i]);
      }

      rows.push(row);
    }

    this.displayedColumns = rows.length ? Object.keys(rows[0]) : [];
    this.tableData = rows;
  }

  private defineColorForCycleType(cycle: NCycle): string {
    switch (cycle) {
      case 'pd':
        return this.primaryDrainageColor;
      case 'fi':
        return this.firstImbibitionColor;
      case 'sd':
        return this.secondaryDrainageColor;
    }
  }

  private defineColorForTable(columnName: string): string {
    const lowerName: string = columnName.toLowerCase();

    // Use cycle-specific colors for all columns
    const prefix: string = lowerName.slice(0, 2);
    switch (prefix) {
      case 'pd':
        return this.defineColorForCycleType('pd');
      case 'fi':
        return this.defineColorForCycleType('fi');
      case 'sd':
        return this.defineColorForCycleType('sd');
      default:
        return this.defaultColor;
    }
  }

  private getSelectedDirections(): Record<string, boolean> {
    return {
      directionX: this.simulationDirection.toLowerCase() === 'x',
      directionY: this.simulationDirection.toLowerCase() === 'y',
      directionZ: this.simulationDirection.toLowerCase() === 'z',
    };
  }

  private getSelectedCycles(): Record<string, boolean> {
    return {
      simulatePrimaryDrainage: this.getCycleControl('pd').value,
      simulateFirstImbibition: this.getCycleControl('fi').value,
      simulateSecondaryDrainage: this.getCycleControl('sd').value,
    };
  }

  private checkIfAtLeastOneCycleSelected(): boolean {
    const selectedCycles: Record<string, boolean> = this.getSelectedCycles();
    return Object.values(selectedCycles).some(Boolean);
  }

  private initCyclesForm(): void {
    this.cyclesForm = this.fb.group({
      pd: [{ value: false, disabled: true }],
      fi: [{ value: false, disabled: true }],
      sd: [{ value: false, disabled: true }],
    });

    merge(
      this.getCycleControl('pd').valueChanges.pipe(map(value => ({ cycle: 'pd', value }))),
      this.getCycleControl('fi').valueChanges.pipe(map(value => ({ cycle: 'fi', value }))),
      this.getCycleControl('sd').valueChanges.pipe(map(value => ({ cycle: 'sd', value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ cycle, value }) => {
        this.updateSeriesWhenSelectedCyclesChanged(cycle as NCycle, value);
        this.updateNTable();
        this.updateTable();
      });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });
  }
}
